import React, {useEffect, useRef, useState} from 'react';
import axios from 'axios';
import {Button} from 'primereact/button';
import {hasRole} from '../../../../utils/hasRole';
import ReceiptPdfViewerComponent from './ReceiptPdfViewerComponent';

export default function ReceiptDocumentPanel({receipt, file, onFileChanged}) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [generated, setGenerated] = useState(false);
  const input = useRef(null);
  const canGenerate = ['CUSTOMER_INVOICE', 'CUSTOMER_ADVANCE_INVOICE', 'CUSTOMER_PROFORMA'].includes(receipt?.receipt_type);
  const canUpload = hasRole('ROLE_RECEIPT_WRITE');
  const fileId = file?.id;
  const showGenerated = canGenerate && (generated || !fileId);
  const headers = () => ({Authorization: 'Bearer ' + localStorage.getItem('userToken'), 'App-Network': localStorage.getItem('networkId')});

  useEffect(() => {
    let alive = true;
    let url;
    setPreview(null);
    setError('');
    if (!receipt?.id || (!fileId && !showGenerated)) return;
    const path = showGenerated ? `/api/v2/receipt/${receipt.id}/download/pdf` : `/api/v2/file/${fileId}/download`;
    axios.get(process.env.REACT_APP_API_HOST + path, {headers: headers(), responseType: 'blob'})
      .then(response => {
        url = URL.createObjectURL(response.data);
        if (alive) setPreview({url, mime: response.data.type || 'application/pdf'});
        else URL.revokeObjectURL(url);
      })
      .catch(() => { if (alive) setError('A bizonylatkép nem tölthető be.'); });
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [receipt?.id, fileId, showGenerated]);

  const upload = async event => {
    const selected = event.target.files?.[0];
    if (!selected) return;
    setBusy(true); setError('');
    try {
      const data = new FormData(); data.append('file', selected);
      const uploaded = await axios.post(process.env.REACT_APP_API_HOST + '/api/v2/file/upload', data, {headers: headers()});
      const saved = await axios.patch(process.env.REACT_APP_API_HOST + `/api/v2/receipt/${receipt.id}/attachment`,
        {file: Number(uploaded.data.data.id)}, {headers: headers()});
      setGenerated(false);
      onFileChanged(saved.data.data.file);
      window.App.toastShow('success', 'Bizonylatkép mentve.');
    } catch (e) {
      setError(e.response?.data?.message || e.response?.data?.detail || 'A feltöltés nem sikerült.');
    } finally {
      setBusy(false); if (input.current) input.current.value = '';
    }
  };

  return <div className="p-p-3">
    <div className="p-d-flex p-flex-wrap p-mb-3" style={{gap: '0.5rem'}}>
      {canUpload && <>
        <input ref={input} type="file" accept="application/pdf,image/*" style={{display: 'none'}} onChange={upload} disabled={busy} />
        <Button type="button" icon="pi pi-upload" label={fileId ? 'Bizonylatkép cseréje' : 'Bizonylatkép feltöltése'} disabled={busy || !receipt?.id} onClick={() => input.current?.click()} />
      </>}
      {fileId && canGenerate && <Button type="button" label={showGenerated ? 'Feltöltött bizonylatkép' : 'Generált PDF'} onClick={() => setGenerated(!showGenerated)} />}
      {preview && <a href={preview.url} download={showGenerated ? `szamla-${receipt.id}.pdf` : (file?.original_filename || 'bizonylat')} className="p-button p-button-secondary">Letöltés</a>}
    </div>
    {error && <p role="alert">{error}</p>}
    {!fileId && !canGenerate && <p>Nincs feltöltött bizonylatkép.</p>}
    {(fileId || showGenerated) && !error && !preview && <p>Betöltés...</p>}
    {preview && (preview.mime.startsWith('image/')
      ? <img src={preview.url} alt="Bizonylatkép" style={{maxWidth: '100%'}} />
      : <ReceiptPdfViewerComponent file={preview.url} height={987} />)}
  </div>;
}
